import { useState, useEffect } from "react";

// const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:5000/api";
const API_BASE = import.meta.env.VITE_API_BASE || "https://i-kept-all-the-code-in-github-and-now-i.onrender.com/api";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const stockStatus = (stock) => {
  if (stock == null) return { label: "Unknown", color: "#888" };
  if (stock === 0) return { label: "Out of Stock", color: "#e53e3e" };
  if (stock <= 5) return { label: `Low (${stock})`, color: "#dd6b20" };
  return { label: `In Stock (${stock})`, color: "#38a169" };
};

// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,8,5,.75)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1714",
          border: "1px solid #3a3228",
          borderRadius: "14px",
          padding: "32px",
          width: "100%",
          maxWidth: "560px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          color: "#e8ddd0",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "none",
            border: "none",
            color: "#888",
            fontSize: "22px",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}

// ─── Product Form ─────────────────────────────────────────────────────────────
const BLANK = {
  name: "", price: "", category: "", subcategory: "",
  fabric: "", description: "", care_instructions: "",
  ethical: "", stock: "", images: "", sizes: "", is_live: false,
};

function ProductForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial || BLANK);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.name.trim() || !form.category.trim()) {
      alert("Name and category are required.");
      return;
    }
    const payload = {
      ...form,
      price: form.price !== "" ? Number(form.price) : null,
      stock: form.stock !== "" ? Number(form.stock) : 0,
      images: form.images
        ? form.images.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      sizes: form.sizes
        ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
    onSave(payload);
  };

  const inputStyle = {
    width: "100%",
    background: "#110f0d",
    border: "1px solid #3a3228",
    borderRadius: "8px",
    padding: "10px 12px",
    color: "#e8ddd0",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const labelStyle = {
    display: "block",
    fontSize: "12px",
    color: "#ba8245",
    marginBottom: "5px",
    letterSpacing: ".06em",
    textTransform: "uppercase",
  };

  const row = (label, key, type = "text", placeholder = "") => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );

  const textarea = (label, key, placeholder = "") => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      <textarea
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        rows={3}
        style={{ ...inputStyle, resize: "vertical" }}
      />
    </div>
  );

  return (
    <div>
      <h2
        style={{
          margin: "0 0 24px",
          fontSize: "20px",
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 400,
          color: "#e8ddd0",
          letterSpacing: ".05em",
        }}
      >
        {initial ? "Edit Product" : "Add Product"}
      </h2>

      {row("Product Name *", "name", "text", "e.g. Handwoven Silk Kurta")}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Price (₹)</label>
          <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} style={inputStyle} placeholder="1499" />
        </div>
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Stock Qty</label>
          <input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} style={inputStyle} placeholder="50" min="0" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {row("Category *", "category", "text", "e.g. Clothing")}
        {row("Subcategory", "subcategory", "text", "e.g. Kurtas")}
      </div>

      {row("Fabric", "fabric", "text", "e.g. 100% Organic Cotton")}
      {row("Sizes (comma-separated)", "sizes", "text", "XS, S, M, L, XL")}
      {row("Image URLs (comma-separated)", "images", "text", "https://...")}
      {textarea("Description", "description")}
      {textarea("Care Instructions", "care_instructions")}
      {textarea("Ethical / Sustainability Info", "ethical")}

      <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
        <div
          onClick={() => set("is_live", !form.is_live)}
          style={{
            width: "42px",
            height: "24px",
            borderRadius: "12px",
            background: form.is_live ? "#ba8245" : "#3a3228",
            cursor: "pointer",
            position: "relative",
            transition: "background .2s",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "3px",
              left: form.is_live ? "21px" : "3px",
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              background: "#fff",
              transition: "left .2s",
            }}
          />
        </div>
        <span style={{ fontSize: "13px", color: form.is_live ? "#ba8245" : "#888" }}>
          {form.is_live ? "Live on store" : "Hidden from store"}
        </span>
      </div>

      <div style={{ display: "flex", gap: "10px" }}>
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            flex: 1,
            padding: "12px",
            background: "linear-gradient(135deg, #ba8245, #8b5a2e)",
            border: "none",
            borderRadius: "8px",
            color: "#fff",
            fontSize: "14px",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
            letterSpacing: ".05em",
          }}
        >
          {loading ? "Saving…" : initial ? "Save Changes" : "Add Product"}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: "12px 20px",
            background: "none",
            border: "1px solid #3a3228",
            borderRadius: "8px",
            color: "#888",
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ open, message, onConfirm, onCancel, dangerous }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onCancel}>
      <div style={{ textAlign: "center", padding: "12px 0" }}>
        <div style={{ fontSize: "40px", marginBottom: "12px" }}>
          {dangerous ? "🗑️" : "📦"}
        </div>
        <p style={{ color: "#e8ddd0", fontSize: "16px", lineHeight: 1.6, marginBottom: "24px" }}>
          {message}
        </p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button
            onClick={onConfirm}
            style={{
              padding: "10px 28px",
              background: dangerous ? "#c53030" : "#ba8245",
              border: "none",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Confirm
          </button>
          <button
            onClick={onCancel}
            style={{
              padding: "10px 28px",
              background: "none",
              border: "1px solid #3a3228",
              borderRadius: "8px",
              color: "#888",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Product Row ──────────────────────────────────────────────────────────────
function ProductRow({ product, onEdit, onDelete, onNoStock, onToggleLive, actionLoading }) {
  const { label, color } = stockStatus(product.stock);
  const isOutOfStock = product.stock === 0 || product.stock == null;
  const busy = actionLoading === product.id;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "60px 1fr 90px 110px 80px 80px auto",
        alignItems: "center",
        gap: "12px",
        padding: "14px 16px",
        background: "#140f0b",
        border: "1px solid #2a2420",
        borderRadius: "10px",
        marginBottom: "8px",
        transition: "border-color .2s",
        opacity: busy ? 0.6 : 1,
      }}
    >
      {/* Image */}
      <div
        style={{
          width: "52px",
          height: "52px",
          borderRadius: "8px",
          background: "#1e1814",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => (e.target.style.display = "none")}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
            }}
          >
            🛍️
          </div>
        )}
      </div>

      {/* Name + category */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "15px",
            color: "#e8ddd0",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {product.name}
        </div>
        <div style={{ fontSize: "11px", color: "#6b665f", marginTop: "2px" }}>
          {product.category}{product.subcategory ? ` › ${product.subcategory}` : ""}
        </div>
      </div>

      {/* Price */}
      <div style={{ fontSize: "14px", color: "#ba8245", fontWeight: 600 }}>
        {fmt(product.price)}
      </div>

      {/* Stock badge */}
      <div>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color,
            background: `${color}18`,
            border: `1px solid ${color}44`,
            padding: "3px 8px",
            borderRadius: "20px",
            letterSpacing: ".04em",
          }}
        >
          {label}
        </span>
      </div>

      {/* Live toggle */}
      <div>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: product.is_live ? "#38a169" : "#888",
            background: product.is_live ? "#38a16918" : "#88888818",
            border: `1px solid ${product.is_live ? "#38a16944" : "#88888844"}`,
            padding: "3px 8px",
            borderRadius: "20px",
          }}
        >
          {product.is_live ? "Live" : "Hidden"}
        </span>
      </div>

      {/* No-stock button */}
      <div>
        {isOutOfStock ? (
          <span style={{ fontSize: "11px", color: "#e53e3e", fontStyle: "italic" }}>
            Out of Stock
          </span>
        ) : (
          <button
            onClick={() => onNoStock(product)}
            disabled={busy}
            title="Mark this product as out of stock (set stock = 0)"
            style={{
              padding: "5px 10px",
              background: "none",
              border: "1px solid #c53030",
              borderRadius: "6px",
              color: "#e53e3e",
              fontSize: "11px",
              fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
              letterSpacing: ".04em",
              whiteSpace: "nowrap",
            }}
          >
            No Stock
          </button>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
        <button
          onClick={() => onToggleLive(product)}
          disabled={busy}
          title={product.is_live ? "Unpublish" : "Publish"}
          style={iconBtn("#ba824522", "#ba8245")}
        >
          {product.is_live ? "⊘" : "◉"}
        </button>
        <button
          onClick={() => onEdit(product)}
          disabled={busy}
          title="Edit product"
          style={iconBtn("#2d4a3e22", "#38a169")}
        >
          ✎
        </button>
        <button
          onClick={() => onDelete(product)}
          disabled={busy}
          title="Delete product"
          style={iconBtn("#c5303022", "#e53e3e")}
        >
          ⌫
        </button>
      </div>
    </div>
  );
}

const iconBtn = (bg, color) => ({
  width: "30px",
  height: "30px",
  background: bg,
  border: `1px solid ${color}44`,
  borderRadius: "6px",
  color,
  fontSize: "14px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background .2s",
});

// ─── Main Admin Products Component ────────────────────────────────────────────
export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | live | hidden | no-stock

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState(null); // { type, product }

  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  // ── fetch ────────────────────────────────────────────────────────────────────
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/products`, {
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (json.success) setProducts(json.products);
    } catch {
      showToast("Failed to load products", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  // ── toast ────────────────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── create / update ──────────────────────────────────────────────────────────
  const handleSave = async (payload) => {
    setFormLoading(true);
    try {
      const url = editing
        ? `${API_BASE}/products/${editing.id}`
        : `${API_BASE}/products`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      showToast(editing ? "Product updated!" : "Product added!");
      setFormOpen(false);
      setEditing(null);
      fetchProducts();
    } catch (err) {
      showToast(err.message || "Save failed", "error");
    } finally {
      setFormLoading(false);
    }
  };

  // ── delete ───────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    const product = confirmData?.product;
    if (!product) return;
    setConfirmOpen(false);
    setActionLoading(product.id);
    try {
      const res = await fetch(`${API_BASE}/products/${product.id}`, { method: "DELETE" });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      showToast("Product deleted");
      fetchProducts();
    } catch (err) {
      showToast(err.message || "Delete failed", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // ── no stock ─────────────────────────────────────────────────────────────────
  const handleNoStockConfirm = async () => {
    const product = confirmData?.product;
    if (!product) return;
    setConfirmOpen(false);
    setActionLoading(product.id);
    try {
      const res = await fetch(`${API_BASE}/products/${product.id}/no-stock`, { method: "PATCH" });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      showToast(`"${product.name}" marked as out of stock`);
      fetchProducts();
    } catch (err) {
      showToast(err.message || "Action failed", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // ── toggle live ──────────────────────────────────────────────────────────────
  const handleToggleLive = async (product) => {
    setActionLoading(product.id);
    try {
      const res = await fetch(`${API_BASE}/products/${product.id}/toggle-live`, { method: "PATCH" });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      showToast(`"${product.name}" is now ${json.product.is_live ? "live" : "hidden"}`);
      fetchProducts();
    } catch (err) {
      showToast(err.message || "Toggle failed", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // ── open confirm ─────────────────────────────────────────────────────────────
  const openConfirm = (type, product) => {
    setConfirmData({ type, product });
    setConfirmOpen(true);
  };

  // ── filtered list ────────────────────────────────────────────────────────────
  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.name.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      (p.subcategory || "").toLowerCase().includes(q);

    const matchFilter =
      filter === "all" ||
      (filter === "live" && p.is_live) ||
      (filter === "hidden" && !p.is_live) ||
      (filter === "no-stock" && (p.stock === 0 || p.stock == null));

    return matchSearch && matchFilter;
  });

  const counts = {
    all: products.length,
    live: products.filter((p) => p.is_live).length,
    hidden: products.filter((p) => !p.is_live).length,
    "no-stock": products.filter((p) => p.stock === 0 || p.stock == null).length,
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0d0b09",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: "#e8ddd0",
        padding: "0 0 60px",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a1410 0%, #0d0b09 100%)",
          borderBottom: "1px solid #2a2420",
          padding: "28px 32px 20px",
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div>
            <div style={{ fontSize: "11px", color: "#ba8245", letterSpacing: ".15em", textTransform: "uppercase", marginBottom: "4px" }}>
              Siyaanish Admin
            </div>
            <h1
              style={{
                margin: 0,
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: "28px",
                fontWeight: 300,
                color: "#e8ddd0",
                letterSpacing: ".04em",
              }}
            >
              Product Management
            </h1>
          </div>
          <button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            style={{
              padding: "10px 22px",
              background: "linear-gradient(135deg, #ba8245, #8b5a2e)",
              border: "none",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              letterSpacing: ".05em",
            }}
          >
            + Add Product
          </button>
        </div>

        {/* Filter tabs */}
        <div
          style={{
            maxWidth: "1100px",
            margin: "20px auto 0",
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {[
            { key: "all", label: "All" },
            { key: "live", label: "Live" },
            { key: "hidden", label: "Hidden" },
            { key: "no-stock", label: "Out of Stock" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "6px 14px",
                border: filter === key ? "1px solid #ba8245" : "1px solid #2a2420",
                borderRadius: "20px",
                background: filter === key ? "#ba824522" : "none",
                color: filter === key ? "#ba8245" : "#888",
                fontSize: "12px",
                cursor: "pointer",
                letterSpacing: ".04em",
              }}
            >
              {label}
              <span
                style={{
                  marginLeft: "6px",
                  background: filter === key ? "#ba8245" : "#2a2420",
                  color: filter === key ? "#fff" : "#666",
                  borderRadius: "10px",
                  padding: "1px 6px",
                  fontSize: "10px",
                }}
              >
                {counts[key]}
              </span>
            </button>
          ))}

          {/* Search */}
          <div style={{ marginLeft: "auto", position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                fontSize: "13px",
              }}
            >
              🔍
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              style={{
                paddingLeft: "30px",
                paddingRight: "12px",
                paddingTop: "6px",
                paddingBottom: "6px",
                background: "#1a1410",
                border: "1px solid #2a2420",
                borderRadius: "20px",
                color: "#e8ddd0",
                fontSize: "12px",
                outline: "none",
                width: "200px",
              }}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "24px 32px" }}>
        {/* Column header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "60px 1fr 90px 110px 80px 80px auto",
            gap: "12px",
            padding: "0 16px",
            marginBottom: "8px",
            fontSize: "10px",
            color: "#6b665f",
            textTransform: "uppercase",
            letterSpacing: ".1em",
          }}
        >
          <div />
          <div>Product</div>
          <div>Price</div>
          <div>Stock</div>
          <div>Status</div>
          <div>Action</div>
          <div />
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#6b665f" }}>
            <div style={{ fontSize: "28px", marginBottom: "12px" }}>⏳</div>
            Loading products…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#6b665f" }}>
            <div style={{ fontSize: "32px", marginBottom: "12px" }}>📭</div>
            No products found
          </div>
        ) : (
          filtered.map((p) => (
            <ProductRow
              key={p.id}
              product={p}
              actionLoading={actionLoading}
              onEdit={(prod) => { setEditing(prod); setFormOpen(true); }}
              onDelete={(prod) => openConfirm("delete", prod)}
              onNoStock={(prod) => openConfirm("no-stock", prod)}
              onToggleLive={handleToggleLive}
            />
          ))
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: toast.type === "error" ? "#c53030" : "#2d4a3e",
            border: `1px solid ${toast.type === "error" ? "#e53e3e" : "#38a169"}`,
            color: "#fff",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "13px",
            zIndex: 2000,
            boxShadow: "0 4px 20px rgba(0,0,0,.4)",
            animation: "fadeIn .2s ease",
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* Add / Edit modal */}
      <Modal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }}>
        <ProductForm
          initial={
            editing
              ? {
                  ...editing,
                  images: (editing.images || []).join(", "),
                  sizes: (editing.sizes || []).join(", "),
                }
              : undefined
          }
          onSave={handleSave}
          onCancel={() => { setFormOpen(false); setEditing(null); }}
          loading={formLoading}
        />
      </Modal>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmOpen && confirmData?.type === "no-stock"}
        message={`Mark "${confirmData?.product?.name}" as out of stock? Stock will be set to 0.`}
        onConfirm={handleNoStockConfirm}
        onCancel={() => setConfirmOpen(false)}
        dangerous={false}
      />
      <ConfirmDialog
        open={confirmOpen && confirmData?.type === "delete"}
        message={`Permanently delete "${confirmData?.product?.name}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
        dangerous={true}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #0d0b09; }
        ::-webkit-scrollbar-thumb { background: #3a3228; border-radius: 3px; }
      `}</style>
    </div>
  );
}